import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, X, Trash2, Upload, Save, AlertTriangle, CheckCircle,
  MapPin, Camera, Fuel, FileText, Edit3, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONE_LABELS = { interior: 'Intérieur', exterior: 'Extérieur', engine: 'Moteur' };
const ZONE_COLORS = { interior: '#0a57a4', exterior: '#099dbb', engine: '#e67e22' };

const DIAGRAM_IMAGES = {
  interior: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/ebi6i96t_plan%20roto.jpg',
  exterior: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/cifznzd0_nautica_roto450s-1024x576.webp',
  engine: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/dnmlb0ol_moteur.png',
};

const ZONE_HEADERS = {
  interior: { label: 'Vue intérieure (dessus)', color: '#0a57a4' },
  exterior: { label: 'Vue extérieure', color: '#099dbb' },
  engine: { label: 'Moteur Yamaha 6CV', color: '#e67e22' },
};

// Interactive boat diagram with real photos
function BoatDiagram({ zone, damages, onClickDiagram, selectedDamage }) {
  const containerRef = useRef(null);

  const handleClick = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onClickDiagram(zone, x, y);
  };

  const zoneDamages = damages.filter(d => d.zone === zone);
  const header = ZONE_HEADERS[zone];

  return (
    <div className="relative border border-[#dec5ae]/40 rounded-sm bg-white overflow-hidden">
      <div className="text-xs font-semibold px-3 py-1.5 border-b border-[#dec5ae]/30"
        style={{ color: header.color, backgroundColor: `${header.color}08` }}>
        {header.label}
      </div>
      <div
        ref={containerRef}
        className="relative cursor-crosshair select-none"
        onClick={handleClick}
        data-testid={`diagram-${zone}`}
      >
        <img
          src={DIAGRAM_IMAGES[zone]}
          alt={header.label}
          className="w-full h-auto block"
          draggable={false}
        />
        {/* Damage markers overlay */}
        {zoneDamages.map((d, i) => (
          <div
            key={d.id}
            className={`absolute flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold shadow-lg border-2 border-white -translate-x-1/2 -translate-y-1/2 pointer-events-none ${selectedDamage === d.id ? 'animate-pulse ring-2 ring-red-400 ring-offset-1' : ''}`}
            style={{ left: `${d.x}%`, top: `${d.y}%` }}
          >
            {i + 1}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ConditionReportTab({ boats, token }) {
  const [selectedBoat, setSelectedBoat] = useState(boats[0]?.id || '');
  const [condition, setCondition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingDamage, setAddingDamage] = useState(null); // {zone, x, y}
  const [damageForm, setDamageForm] = useState({ label: '', description: '' });
  const [selectedDamage, setSelectedDamage] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [expandedDamage, setExpandedDamage] = useState(null);

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchCondition = useCallback(async () => {
    if (!selectedBoat) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/boats/${selectedBoat}/condition`, { headers: authHeaders });
      if (res.ok) setCondition(await res.json());
    } catch { toast.error('Erreur chargement état des lieux'); }
    finally { setLoading(false); }
  }, [selectedBoat, token]);

  useEffect(() => { fetchCondition(); }, [fetchCondition]);

  const saveCondition = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/boats/${selectedBoat}/condition`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({
          fuel_level: condition.fuel_level,
          fuel_type: condition.fuel_type,
          comments: condition.comments,
          has_defects: condition.has_defects
        })
      });
      if (!res.ok) throw new Error();
      toast.success('État des lieux sauvegardé');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleDiagramClick = (zone, x, y) => {
    setAddingDamage({ zone, x, y });
    setDamageForm({ label: '', description: '' });
  };

  const addDamage = async () => {
    if (!damageForm.label.trim()) { toast.error('Veuillez saisir un libellé'); return; }
    try {
      const res = await fetch(`${API}/admin/boats/${selectedBoat}/condition/damage`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ ...addingDamage, ...damageForm })
      });
      if (!res.ok) throw new Error();
      toast.success('Dommage ajouté');
      setAddingDamage(null);
      fetchCondition();
    } catch { toast.error('Erreur ajout dommage'); }
  };

  const deleteDamage = async (damageId) => {
    if (!window.confirm('Supprimer ce dommage ?')) return;
    try {
      const res = await fetch(`${API}/admin/boats/${selectedBoat}/condition/damage/${damageId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast.success('Dommage supprimé');
      fetchCondition();
    } catch { toast.error('Erreur suppression'); }
  };

  const uploadDamagePhoto = async (damageId, file) => {
    setUploadingPhoto(damageId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/admin/boats/${selectedBoat}/condition/damage/${damageId}/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error();
      toast.success('Photo ajoutée');
      fetchCondition();
    } catch { toast.error('Erreur upload photo'); }
    finally { setUploadingPhoto(null); }
  };

  const boatName = boats.find(b => b.id === selectedBoat)?.name || '';
  const damages = condition?.damages || [];

  return (
    <div className="space-y-6" data-testid="condition-report-tab">
      {/* Boat selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Select value={selectedBoat} onValueChange={setSelectedBoat}>
            <SelectTrigger className="w-[200px]" data-testid="condition-boat-select">
              <SelectValue placeholder="Sélectionner un bateau" />
            </SelectTrigger>
            <SelectContent>
              {boats.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.name} - {b.model}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {damages.length > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">
              <AlertTriangle className="w-3 h-3" /> {damages.length} défaut(s)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3" /> Aucun défaut
            </span>
          )}
        </div>
        <Button onClick={saveCondition} disabled={saving} className="bg-[#0a57a4] hover:bg-[#099dbb] text-white" data-testid="save-condition-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#858275]">Chargement...</div>
      ) : condition && (
        <>
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-800">
            <strong>Mode d'emploi :</strong> Cliquez directement sur le schéma du bateau pour ajouter un marqueur de dommage. Chaque zone (intérieur, extérieur, moteur) est indépendante.
          </div>

          {/* Diagrams grid */}
          <div className="grid lg:grid-cols-3 gap-4">
            <BoatDiagram zone="interior" damages={damages} onClickDiagram={handleDiagramClick} selectedDamage={selectedDamage} />
            <BoatDiagram zone="exterior" damages={damages} onClickDiagram={handleDiagramClick} selectedDamage={selectedDamage} />
            <BoatDiagram zone="engine" damages={damages} onClickDiagram={handleDiagramClick} selectedDamage={selectedDamage} />
          </div>

          {/* Fuel & comments */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-sm border border-[#dec5ae]/30 space-y-3">
              <h4 className="text-sm font-semibold text-[#0a57a4] flex items-center gap-1"><Fuel className="w-4 h-4" /> Carburant</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#858275]">Niveau (litres)</Label>
                  <Input type="number" value={condition.fuel_level || 12}
                    onChange={e => setCondition(p => ({ ...p, fuel_level: parseInt(e.target.value) || 0 }))}
                    className="text-sm" data-testid="fuel-level-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#858275]">Type</Label>
                  <Input value={condition.fuel_type || 'Sans plomb 95'}
                    onChange={e => setCondition(p => ({ ...p, fuel_type: e.target.value }))}
                    className="text-sm" />
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-sm border border-[#dec5ae]/30 space-y-3">
              <h4 className="text-sm font-semibold text-[#0a57a4] flex items-center gap-1"><FileText className="w-4 h-4" /> Commentaires</h4>
              <Textarea value={condition.comments || ''}
                onChange={e => setCondition(p => ({ ...p, comments: e.target.value }))}
                rows={3} className="text-sm resize-none" placeholder="Commentaires généraux sur l'état du bateau..."
                data-testid="condition-comments" />
            </div>
          </div>

          {/* Damages list */}
          <div className="bg-white rounded-sm border border-[#dec5ae]/30 overflow-hidden">
            <div className="px-4 py-3 bg-[#FDFBF7] border-b border-[#dec5ae]/30 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#0a57a4] flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Liste des défauts ({damages.length})
              </h4>
            </div>
            {damages.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#858275]">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                Aucun défaut enregistré. Cliquez sur un schéma pour ajouter un dommage.
              </div>
            ) : (
              <div className="divide-y divide-[#dec5ae]/20">
                {damages.map((d, i) => (
                  <div key={d.id} className="group" onMouseEnter={() => setSelectedDamage(d.id)} onMouseLeave={() => setSelectedDamage(null)}>
                    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FDFBF7]/60 transition-colors">
                      <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0"
                        style={{ backgroundColor: ZONE_COLORS[d.zone] || '#999' }}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{ZONE_LABELS[d.zone]}</span>
                          <span className="text-sm font-medium text-[#0a57a4] truncate">{d.label}</span>
                        </div>
                        {d.description && <p className="text-xs text-[#858275] mt-0.5 truncate">{d.description}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {d.photos?.length > 0 && (
                          <span className="text-xs text-[#858275]"><Camera className="w-3 h-3 inline" /> {d.photos.length}</span>
                        )}
                        <button onClick={() => setExpandedDamage(expandedDamage === d.id ? null : d.id)} className="p-1 hover:bg-gray-100 rounded">
                          {expandedDamage === d.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => deleteDamage(d.id)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <AnimatePresence>
                      {expandedDamage === d.id && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden border-t border-[#dec5ae]/20 bg-[#FDFBF7]/50">
                          <div className="px-4 py-3 space-y-2">
                            {d.description && <p className="text-xs text-[#858275]">{d.description}</p>}
                            {d.photos?.length > 0 && (
                              <div className="flex gap-2 flex-wrap">
                                {d.photos.map((url, pi) => (
                                  <a key={pi} href={url} target="_blank" rel="noopener noreferrer"
                                    className="w-16 h-16 rounded border border-[#dec5ae]/30 overflow-hidden">
                                    <img src={url} alt={`Dommage ${i + 1}`} className="w-full h-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-2 pt-1">
                              <label className="flex items-center gap-1 text-xs px-2 py-1 bg-white border border-[#dec5ae]/30 rounded cursor-pointer hover:bg-gray-50">
                                <Camera className="w-3 h-3" />
                                {uploadingPhoto === d.id ? 'Upload...' : 'Ajouter photo'}
                                <input type="file" accept="image/*" className="hidden"
                                  onChange={e => e.target.files[0] && uploadDamagePhoto(d.id, e.target.files[0])} />
                              </label>
                              <span className="text-xs text-[#858275]">Position: {Math.round(d.x)}%, {Math.round(d.y)}%</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add damage dialog */}
      <Dialog open={!!addingDamage} onOpenChange={() => setAddingDamage(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0a57a4] flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Ajouter un dommage
            </DialogTitle>
          </DialogHeader>
          {addingDamage && (
            <div className="space-y-3">
              <div className="bg-gray-50 p-2 rounded text-xs text-[#858275]">
                Zone : <strong className="text-[#0a57a4]">{ZONE_LABELS[addingDamage.zone]}</strong> | Position : ({Math.round(addingDamage.x)}%, {Math.round(addingDamage.y)}%)
              </div>
              <div>
                <Label className="text-xs text-[#858275]">Libellé *</Label>
                <Input value={damageForm.label} onChange={e => setDamageForm(p => ({ ...p, label: e.target.value }))}
                  placeholder="Ex: Rayure bâbord, Choc proue..." className="text-sm" data-testid="damage-label-input" />
              </div>
              <div>
                <Label className="text-xs text-[#858275]">Description</Label>
                <Textarea value={damageForm.description} onChange={e => setDamageForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Détails supplémentaires..." rows={2} className="text-sm resize-none" />
              </div>
              <Button onClick={addDamage} className="w-full bg-[#0a57a4] hover:bg-[#099dbb] text-white" data-testid="confirm-add-damage">
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
